import { useState, useEffect, useCallback } from "react";
import { useApp } from "@/contexts/AppContext";
import { usePageTitle } from "@/components/common/AppShell";
import Modal from "@/components/common/Modal";
import ProviderForm from "@/components/providers/ProviderForm";
import DataTable, { DATA_TABLE_CLASSES } from "@/components/common/DataTable";
import Card from "@/components/common/Card";
import ActionMenu from "@/components/common/ActionMenu";
import { Plus, Pencil, Trash2, Building2 } from "lucide-react";
import RowIcon from "@/components/common/RowIcon";

const KIND_LABELS = { 1: "Dynamic", 2: "Overlay", 4: "Banner", 8: "Static" };

function kindsLabel(mask) {
  if (!mask) return "—";
  if (mask === 15) return "All";
  return Object.entries(KIND_LABELS)
    .filter(([bit]) => (mask & Number(bit)) !== 0)
    .map(([, label]) => label)
    .join(", ");
}

export default function ProvidersPage() {
  usePageTitle("Providers");
  const { performRequest } = useApp();

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await performRequest("/providers");
      setProviders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load providers", err);
      setError(err.message || "Failed to load providers");
    } finally {
      setLoading(false);
    }
  }, [performRequest]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const handleCreate = () => {
    setEditingProvider(null);
    setIsModalOpen(true);
  };

  const handleEdit = (provider) => {
    setEditingProvider(provider);
    setIsModalOpen(true);
  };

  const handleDelete = async (provider) => {
    if (!window.confirm(`Delete provider "${provider.name}"?`)) return;
    try {
      await performRequest(`/providers/${provider.uuid}`, { method: "DELETE" });
      setProviders((prev) => prev.filter((p) => p.uuid !== provider.uuid));
    } catch (err) {
      console.error("Failed to delete provider", err);
      window.alert(
        "Failed to delete provider: " + (err.message || "Unknown error"),
      );
    }
  };

  const handleSave = async (formValues) => {
    try {
      if (editingProvider) {
        const updated = await performRequest(
          `/providers/${editingProvider.uuid}`,
          {
            method: "PUT",
            body: JSON.stringify(formValues),
          },
        );
        setProviders((prev) =>
          prev.map((p) => (p.uuid === editingProvider.uuid ? updated : p)),
        );
      } else {
        const created = await performRequest("/providers", {
          method: "POST",
          body: JSON.stringify(formValues),
        });
        setProviders((prev) => [...prev, created]);
      }
      setIsModalOpen(false);
      setEditingProvider(null);
    } catch (err) {
      console.error("Failed to save provider", err);
      window.alert(
        "Failed to save provider: " + (err.message || "Unknown error"),
      );
    }
  };

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-base text-(--text-secondary)">
          Manage ad network providers and their rates.
        </p>
        <button
          type="button"
          onClick={handleCreate}
          className="inline-flex items-center gap-2 rounded-(--radius-md) border-0 bg-(--brand-primary) px-[18px] py-[10px] text-[13px] font-bold text-(--brand-primary-contrast) outline-none transition-colors hover:bg-(--brand-primary-hover) focus:outline-none"
        >
          <Plus size={16} strokeWidth={2.5} />
          Create provider
        </button>
      </div>

      {error && (
        <Card className="mb-4">
          <p className="text-sm text-(--status-danger)">{error}</p>
        </Card>
      )}

      {loading ? (
        <Card>
          <p className="text-sm text-(--text-secondary)">Loading providers…</p>
        </Card>
      ) : (
        <DataTable>
          <thead>
            <tr className={DATA_TABLE_CLASSES.headRow}>
              <th className={DATA_TABLE_CLASSES.th}>Name</th>
              <th className={DATA_TABLE_CLASSES.th}>Kinds</th>
              <th className={DATA_TABLE_CLASSES.th}>CPM ($)</th>
              <th className={DATA_TABLE_CLASSES.th}>Created</th>
              <th className={DATA_TABLE_CLASSES.thRight}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {providers.length === 0 ? (
              <tr>
                <td colSpan="5" className={DATA_TABLE_CLASSES.emptyCell}>
                  No providers yet. Add your first provider to get started.
                </td>
              </tr>
            ) : (
              providers.map((provider) => (
                <tr key={provider.uuid} className={DATA_TABLE_CLASSES.bodyRow}>
                  <td className={DATA_TABLE_CLASSES.td}>
                    <div className="flex items-center gap-3">
                      <RowIcon icon={Building2} />
                      <span className="font-semibold">{provider.name}</span>
                    </div>
                  </td>
                  <td
                    className={`${DATA_TABLE_CLASSES.td} text-(--text-secondary)`}
                  >
                    {kindsLabel(provider.kinds)}
                  </td>
                  <td className={DATA_TABLE_CLASSES.td}>
                    {provider.cpm != null
                      ? Number(provider.cpm).toFixed(2)
                      : "—"}
                  </td>
                  <td
                    className={`${DATA_TABLE_CLASSES.td} text-(--text-secondary)`}
                  >
                    {provider.createdAt
                      ? new Date(provider.createdAt).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className={DATA_TABLE_CLASSES.tdRight}>
                    <ActionMenu
                      ariaLabel={`Actions for ${provider.name}`}
                      items={[
                        {
                          label: "Edit",
                          icon: Pencil,
                          onClick: () => handleEdit(provider),
                        },
                        {
                          label: "Delete",
                          icon: Trash2,
                          danger: true,
                          onClick: () => handleDelete(provider),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </DataTable>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingProvider(null);
        }}
        title={editingProvider ? "Edit Provider" : "Create Provider"}
        size="medium"
      >
        <ProviderForm
          key={editingProvider?.uuid ?? "new"}
          initialProvider={editingProvider}
          onSubmit={handleSave}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingProvider(null);
          }}
        />
      </Modal>
    </section>
  );
}
